import * as React from 'react'
import { useEffect, useContext } from 'react'
import RemoveFieldsButton from '../shared/RemoveFieldsButton'
import MinutesPicker from './MinutesPicker'
import HoursPicker from './HoursPicker'
import RemovedField from './RemovedField'
import QuickTimePicker from './QuickTimePicker'
import PlusMinusToggle from './PlusMinusToggle'
import WorktypePicker from './WorktypePicker'
import { separateTime } from '../helpers/time'
import CommonContext from '../CommonContext'

type TimeFieldsProps = {
  idKey: string
  timeFields: {
    id?: number
    time: number
    worktype_id: number
    comment: string
    _destroy?: boolean
    partner_id?: number
    partner_time?: boolean
    record_id?: number
    exported?: boolean
    approved?: boolean
  }
  setTimeFields: (timeFields: any) => void
  showSignControl: boolean
  availableWorktypes: [string, number][]
  fieldNameFn: (field: string) => string
  handleRemove: () => void
  allowRemove: boolean
  className?: string
}

export default function TimeFields(props: TimeFieldsProps) {
  const { className, idKey, timeFields, setTimeFields, showSignControl, availableWorktypes, fieldNameFn, handleRemove, allowRemove } = props
  const context = useContext(CommonContext)
  const allowTwoMinutes = context?.currentUser?.allow_two_minutes_time_registrations ?? false
  const timeId = timeFields.id || null
  const worktypeId = timeFields.worktype_id || ''
  const comment = timeFields.comment || ''
  let signMultiplier = timeFields.time && timeFields.time < 0 ? -1 : 1
  const [hours, minutes] = separateTime(Math.abs(timeFields.time))
  useEffect(() => {
    signMultiplier = timeFields.time && timeFields.time < 0 ? -1 : 1
  }, [timeFields.time])
  const fieldId = (field: string) => `${idKey}-field-${field}`
  const partnerId = timeFields.partner_id || null

  if (timeFields._destroy === true) {
    return <RemovedField field={{ id: timeFields.id as number }} fieldNameFn={fieldNameFn} />
  }

  const isPartnerTime = timeFields.partner_time || false
  const disabled = isPartnerTime && (!!timeFields.exported || !!timeFields.approved)
  const minAllowedValue = allowTwoMinutes ? 2 : 5

  return (
    <fieldset className={`position-relative form-fieldset ${className}`}>
      <WorktypePicker
        id={fieldId('worktype_id')}
        name={fieldNameFn('worktype_id')}
        partnerId={partnerId}
        isPartnerTime={isPartnerTime}
        availableWorktypes={availableWorktypes}
        worktypeId={worktypeId}
        setWorktypeId={id => setTimeFields({ ...timeFields, worktype_id: id })}
        disabled={disabled}
      />
      <div className="row">
        <div className="col-sm-12 form-group">
          <label htmlFor={fieldId('comment')}>Beskrivelse</label>
          <input
            id={fieldId('comment')}
            name={fieldNameFn('comment')}
            className="form-control resources-time-comment"
            value={comment}
            disabled={disabled}
            onChange={e => setTimeFields({ ...timeFields, comment: e.target.value })}
          />
        </div>
      </div>
      <div className="row">
        <QuickTimePicker
          disabled={disabled}
          className="col-md-6 form-group"
          setTime={time => setTimeFields({ ...timeFields, time })}
          allowTwoMinutes={allowTwoMinutes}
        />
        <div className="col-md-6">
          <div className="resources-time-selects">
            <div className="form-group w-100">
              <label className="d-flex justify-content-between">
                <span className="d-inline-block mr-2">Tid</span>
              </label>

              <div className="d-flex">
                {showSignControl && (
                  <PlusMinusToggle
                    signMultiplier={signMultiplier}
                    onSignChange={newSignMultiplier => {
                      const newTime =
                        timeFields.time === undefined || timeFields.time === 0
                          ? newSignMultiplier * 60
                          : newSignMultiplier * Math.abs(timeFields.time)
                      setTimeFields({ ...timeFields, time: newTime })
                    }}
                  />
                )}
                <div className="mr-2 time-picker-field">
                  <HoursPicker
                    hours={hours}
                    disabled={disabled}
                    setHours={h => setTimeFields({ ...timeFields, time: (h * 60 + minutes) * signMultiplier })}
                    name={fieldNameFn('hours')}
                  />
                </div>

                <div className="time-picker-field">
                  <MinutesPicker
                    minutes={minutes}
                    setMinutes={m => setTimeFields({ ...timeFields, time: (hours * 60 + m) * signMultiplier })}
                    name={fieldNameFn('minutes')}
                    disabled={disabled}
                    allowTwoMinutes={allowTwoMinutes}
                  />
                  <input className="invisible-field" type="number" onChange={() => {}} value={hours * 60 + minutes} min={minAllowedValue} />
                </div>
              </div>
            </div>
            <input
              type="hidden"
              disabled={disabled}
              name={fieldNameFn('time')}
              value={(hours * 60 + minutes) * signMultiplier}
              min={minAllowedValue}
            />
            {timeId && <input type="hidden" disabled={disabled} name={fieldNameFn('id')} value={timeId} />}
          </div>
        </div>
      </div>

      {allowRemove && !disabled && <RemoveFieldsButton className="resources-remove-fields-button" handleRemove={handleRemove} />}
    </fieldset>
  )
}
